<template>
  <div>
    <h4 class="text-center text-lighter font-weight-normal mt-5 mb-0">Login to Your Account</h4>

    <AppWait v-if="initialLoading" />

    <!-- Form -->
    <form class="mt-5 mb-4" v-if="!initialLoading && passwordAuthEnabled">
      <b-form-group class="mb-0" v-if="errors.common">
        <p class="text-danger">{{ errors.common }}</p>
      </b-form-group>
      <b-form-group label="Email" :feedback="errors.email">
        <b-input v-model="email" tabindex="1" :state="!errors.email" />
      </b-form-group>
      <b-form-group :feedback="errors.password">
        <div slot="label" class="d-flex justify-content-between align-items-end">
          <div>Password</div>
          <router-link :to="{ name: 'password-reset-request', query: { email } }" class="d-block small">Forgot password?</router-link>
        </div>
        <b-input type="password" v-model="password" tabindex="2" :state="!errors.password" />
      </b-form-group>

      <div class="d-flex justify-content-between align-items-center m-0">
        <b-btn variant="primary" @click="login">Sign In</b-btn>
      </div>
    </form>
    <!-- / Form -->

    <div class="extra-actions" v-if="!initialLoading">
      <b-btn v-if="canSamlLogin" variant="default" @click="goToSamlLogin">
        {{ samlButtonLabel }}
      </b-btn>
      <b-btn v-if="!canSamlLogin" variant="primary ms-login-button" @click="goToMsLogin">
        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
        Sign In with Microsoft
      </b-btn>
      <div class="text-center text-muted" v-if="hasWorkspaceCreate">
        Don't have an workspace yet?
        <router-link :to="{ name: 'register' }">Create Workspace</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { goToMsLogin, isLastSignIntranet } from '@/views/auth/utils'
import { get } from '@/utils/Api'
import AppWait from '@/components/shared/AppWait'

const SAML_USER_HASH = '#saml_user='

export default {
  name: 'login',
  components: {
    AppWait
  },
  data: () => ({
    errors: {},
    email: '',
    password: '',
    samlEnabled: false,
    samlButtonLabel: '',
    passwordAuthEnabled: true,
    initialLoading: true
  }),
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters(['frontDomain', 'workspaceDomain']),
    ...mapGetters('auth', ['isUserOnOwnDomain']),
    hasWorkspaceCreate() {
      return !this.workspaceDomain
    },
    samlReachable() {
      return !!this.workspaceDomain
    },
    canSamlLogin() {
      return this.samlReachable && this.samlEnabled
    }
  },
  methods: {
    ...mapActions('auth', { _login: 'login', loginByUser: 'loginByUser' }),
    goToMsLogin,
    goToSamlLogin() {
      window.location = `https://${this.workspaceDomain}.${this.frontDomain}/api/v1/saml/login`
    },
    hasSamlHash() {
      return window.location.hash.startsWith(SAML_USER_HASH)
    },
    login() {
      if (this.validate()) {
        this._login({ email: this.email, password: this.password }).then(() => {
            this.handleSuccessLogin()
          }, (error) => {
            if (error.response && error.response.data) {
              if (error.response.status === 423) return
              let data = error.response.data
              // check verification error
              if (data && data.errors && data.errors.password && data.errors.password[0] === 'unverified') {
                this.$router.push({ name: 'register-verification' })
              } else {
                // other errors
                let message = ''
                for (let field in data.errors) {
                  message += data.errors[field].join(' ')
                }
                Vue.set(this.errors, 'common', message)
              }
            } else {
              throw error
            }
          })
      }
    },
    handleSuccessLogin() {
      let referer = this.$route.query.referer
      let url = referer ? referer.replace(`${location.protocol}//${location.hostname}`, '') : '/'
      window.location = `https://${this.user.domain}.${this.frontDomain}${url}`
    },
    validate() {
      let isValid = true
      Vue.set(this, 'errors', {})
      if (!this.email) {
        isValid = false
        Vue.set(this.errors, 'email', 'Email required')
      }

      if (!this.password) {
        isValid = false
        Vue.set(this.errors, 'password', 'Password required')
      }
      return isValid
    },
    getError(field) {
      return this.errors[field]
    },
    loadSamlLoginMetadata() {
      this.initialLoading = true
      get('/saml/login-metadata')
        .then((r) => {
          this.samlButtonLabel = r.data?.buttonLabel
          this.samlEnabled = r.data?.enabled
          this.passwordAuthEnabled = r.data?.passwordAuthEnabled
        })
        .finally(() => {
          this.initialLoading = false
        })
    },
    tryToLoginBySamlHash() {
      try {
        this.initialLoading = true
        const hashValue = window.location.hash.replace(SAML_USER_HASH, '')
        const user = JSON.parse(atob(hashValue))
        this.loginByUser(user)
          .then(() => this.handleSuccessLogin())
          .finally(() => { this.initialLoading = false })
      } catch (e) {
        console.error('Saml Login Failed: ' + e.message)
        this.loadSamlLoginMetadata()
      }
    }
  },
  mounted() {
    if (this.hasSamlHash()) {
      this.tryToLoginBySamlHash()
    } else {
      this.loadSamlLoginMetadata()
    }

    if (isLastSignIntranet()) {
      this.$router.push({ name: 'login-intranet' })
    } else if (this.$route.query && this.$route.query.email) {
      this.email = this.$route.query.email
    }
  },
}
</script>

<style lang="scss">
.extra-actions {
  display: flex;
  margin: 0;
  margin-top: 16px;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
}

.ms-login-button {
  display: flex;
  align-items: center;
  height: 41px;
  padding: 0 12px;
  border-radius: 0;
  border: 1px solid #8c8c8c;
  background: #fff;
  color: #5e5e5e;
  font-family: 'Segoe UI';
  font-size: 15px;
  margin: 0 auto 20px;
  white-space: nowrap;

  & > svg {
    margin-right: 12px;
    flex-shrink: 0;
  }
}
</style>
